import { Gym } from 'fitbeat.models';
import moment from 'moment-timezone';
import React from 'react';
import { Filter } from 'react-table';
import { aggregateFilterSelectHandler } from '../components/activeFilter';
import DatePickerPreDefinedDays from '../components/datePickerPreDefinedDays';
import GreaterThanLessThanFilter from '../components/greaterThanLessThanFilter';
import { MemberTableHeader } from '../components/memberTableHeader';
import {
  filterMethodContainsOrDoesntContain,
  filterMethodDateRange,
} from './filterHelpers';

const formatJsonForCell = (value: unknown): string => {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value !== 'object') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(formatJsonForCell).filter(Boolean).join('; ');
  }
  return Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue != null && entryValue !== '')
    .map(([key, entryValue]) => {
      const formatted = typeof entryValue === 'object'
        ? JSON.stringify(entryValue)
        : String(entryValue);
      return `${key}:${formatted}`;
    })
    .join(', ');
};

const getMetaDataField = (metaData: unknown, field: string): string => {
  if (!metaData || typeof metaData !== 'object' || Array.isArray(metaData)) {
    return '';
  }
  const value = (metaData as Record<string, unknown>)[field];
  return value == null ? '' : String(value);
};

const getMetaDataFieldForAggregateFilter = (metaData: unknown, field: string): string | undefined => {
  if (!metaData || typeof metaData !== 'object' || Array.isArray(metaData)) {
    return undefined;
  }
  const value = (metaData as Record<string, unknown>)[field];
  return value == null ? undefined : String(value);
};

export const buildExperiencePointsConfigColumnsFromAccessor = (currentGym: Gym) => [
  {
    id: 'createdAt',
    Header: () => <MemberTableHeader header='Awarded At' />,
    accessor: 'createdAt',
    used: true,
    headerClassName: 'container-overflow-visible',
    filterable: true,
    sortable: true,
    width: 220,
    filterMethod: (filter: Filter, row: any) =>
      filterMethodDateRange(filter, row._original.createdAt, currentGym.timezone),
    Filter: ({ filter, onChange }: any) => (
      <DatePickerPreDefinedDays filter={filter} onChange={onChange} type='createdAt' />
    ),
    Cell: ({ original }: any) => (original.createdAt
      ? moment.tz(original.createdAt, currentGym.timezone).format('YYYY-MM-DD HH:mm')
      : ''),
  },
  {
    id: 'date',
    Header: () => <MemberTableHeader header='Event Date' />,
    accessor: 'date',
    used: true,
    headerClassName: 'container-overflow-visible',
    filterable: true,
    sortable: true,
    width: 220,
    filterMethod: (filter: Filter, row: any) =>
      filterMethodDateRange(filter, row._original.date, currentGym.timezone),
    Filter: ({ filter, onChange }: any) => (
      <DatePickerPreDefinedDays filter={filter} onChange={onChange} type='date' />
    ),
    Cell: ({ original }: any) => original.date || '',
  },
  {
    id: 'displayTitle',
    Header: () => <MemberTableHeader header='Title' />,
    accessor: 'displayTitle',
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'displayTitle'),
    filterAll: true,
    Cell: ({ original }: any) => original.displayTitle || original.event || '',
    filterable: true,
    sortable: true,
    width: 280,
  },
  {
    id: 'event',
    Header: () => <MemberTableHeader header='Event Type' />,
    accessor: 'event',
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'event'),
    filterAll: true,
    Cell: ({ original }: any) => original.event || '',
    filterable: true,
    sortable: true,
    width: 200,
  },
  {
    id: 'category',
    Header: () => <MemberTableHeader header='Category' />,
    accessor: 'category',
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'category'),
    filterAll: true,
    Cell: ({ original }: any) => original.category || '',
    filterable: true,
    sortable: true,
    width: 120,
  },
  {
    id: 'points',
    Header: () => <MemberTableHeader header='Points' />,
    accessor: 'points',
    used: true,
    filterMethod: (filter: Filter, row: any) => {
      const updatedRow = { ...row };
      return aggregateFilterSelectHandler(filter, updatedRow);
    },
    Filter: ({ filter, onChange }: any) => (
      <GreaterThanLessThanFilter filter={filter} onChange={onChange} columnId='points' />
    ),
    headerClassName: 'container-overflow-visible',
    filterable: true,
    sortable: true,
    Cell: ({ original }: any) => (original.points >= 0 ? original.points : ''),
    width: 220,
  },
  {
    id: 'pointsUncapped',
    Header: () => <MemberTableHeader header='Points (Uncapped)' />,
    accessor: 'pointsUncapped',
    used: true,
    filterMethod: (filter: Filter, row: any) => {
      const updatedRow = { ...row };
      return aggregateFilterSelectHandler(filter, updatedRow);
    },
    Filter: ({ filter, onChange }: any) => (
      <GreaterThanLessThanFilter filter={filter} onChange={onChange} columnId='pointsUncapped' />
    ),
    headerClassName: 'container-overflow-visible',
    filterable: true,
    sortable: true,
    Cell: ({ original }: any) => (original.pointsUncapped >= 0 ? original.pointsUncapped : ''),
    width: 220,
  },
  {
    id: 'referenceId',
    Header: () => <MemberTableHeader header='Reference ID' />,
    accessor: 'referenceId',
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'referenceId'),
    filterAll: true,
    Cell: ({ original }: any) => original.referenceId || '',
    filterable: true,
    sortable: true,
    width: 220,
  },
  {
    id: 'memberMainGym',
    Header: () => <MemberTableHeader header='Member Main Gym' />,
    accessor: 'memberMainGym',
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'memberMainGym'),
    filterAll: true,
    Cell: ({ original }: any) => original.memberMainGym || '',
    filterable: true,
    sortable: true,
    width: 160,
  },
  {
    id: 'limitDetails',
    Header: () => <MemberTableHeader header='Limit Details' />,
    accessor: (row: any) => formatJsonForCell(row?.limitDetails),
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'limitDetails'),
    filterAll: true,
    Cell: ({ original }: any) => formatJsonForCell(original.limitDetails),
    filterable: true,
    sortable: true,
    width: 280,
  },
  {
    id: 'stepCount',
    Header: () => <MemberTableHeader header='Step Count' />,
    accessor: (row: any) => getMetaDataField(row?.metaData, 'steps'),
    used: true,
    filterMethod: (filter: Filter, row: any) => {
      const updatedRow = { ...row };
      updatedRow[filter.id] = getMetaDataFieldForAggregateFilter(row._original?.metaData, 'steps');
      return aggregateFilterSelectHandler(filter, updatedRow);
    },
    Filter: ({ filter, onChange }: any) => (
      <GreaterThanLessThanFilter filter={filter} onChange={onChange} columnId='stepCount' />
    ),
    headerClassName: 'container-overflow-visible',
    filterable: true,
    sortable: true,
    Cell: ({ original }: any) => getMetaDataField(original.metaData, 'steps'),
    width: 220,
  },
  {
    id: 'importSource',
    Header: () => <MemberTableHeader header='Import Source' />,
    accessor: (row: any) => getMetaDataField(row?.metaData, 'source'),
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'importSource'),
    filterAll: true,
    Cell: ({ original }: any) => getMetaDataField(original.metaData, 'source'),
    filterable: true,
    sortable: true,
    width: 160,
  },
  {
    id: 'achievements',
    Header: () => <MemberTableHeader header='Achievements' />,
    accessor: (row: any) => formatJsonForCell(row?.achievements),
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'achievements'),
    filterAll: true,
    Cell: ({ original }: any) => formatJsonForCell(original.achievements),
    filterable: true,
    sortable: true,
    width: 220,
  },
  {
    id: 'leaderboards',
    Header: () => <MemberTableHeader header='Leaderboards' />,
    accessor: (row: any) => formatJsonForCell(row?.leaderboards),
    used: true,
    filterMethod: (filter: Filter, rows: any) =>
      filterMethodContainsOrDoesntContain(filter, rows, 'leaderboards'),
    filterAll: true,
    Cell: ({ original }: any) => formatJsonForCell(original.leaderboards),
    filterable: true,
    sortable: true,
    width: 280,
  },
];
